import { NamedStateDeclaration, RoutingItem, RoutingItemOptions } from '../model/routing-item';
import { StateProvider, StateService, TransitionService, UrlRouter, UrlRouterProvider } from '@uirouter/angularjs';
import { IQService, ITimeoutService, IWindowService } from 'angular';
import { ConfigService } from './mx-config-service';
import { EventService } from './event-service';
import { EventCodes } from '../model/event-codes';
import { RawParams } from '@uirouter/core/lib/params/interface';
/**
 * @ngdoc service
 * @kind function
 * @name RoutingService
 * @module mx.app
 *
 * @requires <a href="https://docs.angularjs.org/api/ng/service/$q" target="_blank">$q</a>
 * @requires <a href="http://angular-ui.github.io/ui-router/site/#/api/ui.router.state.$state" target="_blank">$state</a>
 * @requires <a href="http://angular-ui.github.io/ui-router/site/#/api/ui.router.router.$urlRouter" target="_blank">$urlRouter</a>
 * @requires <a href="https://docs.angularjs.org/api/ng/service/$window" target="_blank">$window</a>
 * @requires {@ref mx.shell.Config mx.shell.Config}
 * @requires {@ref mx.shell.EventService mx.shell.EventService}
 * @requires {@ref mx.shell.AuthService mx.shell.AuthService}
 *
 * @description
 * A service provides methods for managing navigation routes
 */

type StateParams = NamedStateDeclaration['params'];

export class RoutingHistory {
	private _current?: { state: NamedStateDeclaration; parameters?: StateParams };
	private _last?: { state: NamedStateDeclaration; parameters?: StateParams };

	get last() {
		return this._last;
	}

	get current() {
		return this._current;
	}
	set current(value) {
		// TODO probably full history is required too?
		if (this._current) {
			this._last = this._current;
		}
		this._current = value;
	}
}

type BaseRouteType = { name: string; parameters: RoutingItem['params'] };

export class RoutingService {
	private readonly routeConfigs: Record<string, NamedStateDeclaration> = {};
	private readonly history: RoutingHistory;
	private defaultRoute?: BaseRouteType;
	private stateProvidernitialized = false;
	public landingPageRoute?: RoutingItem;
	public searchPageRoute?: RoutingItem;

	constructor(
		private readonly $timeout: ITimeoutService,
		private readonly $q: IQService,
		private readonly $window: IWindowService,
		private readonly $state: StateService,
		private readonly $urlRouter: UrlRouter,
		private readonly config: ConfigService,
		private readonly eventService: EventService,
		private readonly $transitions: TransitionService,
		private readonly $stateProvider: StateProvider,
		private readonly $urlRouterProvider: UrlRouterProvider
	) {
		this.history = new RoutingHistory();

		this.eventService.subscribeGlobal(EventCodes.NavigationLoaded, (event, { routes, defaultRoute }) => {
			if (!Array.isArray(routes)) {
				return;
			}
			this.addItems(routes);
			this.defaultRoute = this.defaultRoute || defaultRoute;
		});

		this.initTransitionEvents();
	}

	private initTransitionEvents() {
		this.$transitions.onBefore(
			{
				entering: () => true,
			},
			async () => {
				this.eventService.dispatchEvent(EventCodes.RouteLoading);
			},
			{ priority: 100 }
		);

		let currentRouteUrlCache = '';
		const getCurrentState = (stateName: string) => this.routeConfigs[stateName] || this.$state.current || {};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const setCurrentPageBodyAttribute = (currentRoute: any) => {
			if (currentRouteUrlCache !== currentRoute.url) {
				currentRouteUrlCache = currentRoute.url;
				document.body.setAttribute('current-page', currentRoute.url);
			}
		};
		this.$transitions.onSuccess(
			{
				entering: () => true,
			},
			async (transition) => {
				const stateName = transition.to().name || '';

				const current = getCurrentState(stateName);
				this.history.current = { state: current, parameters: transition.to().params };
				this.eventService.dispatchEvent(EventCodes.RouteActivated, current);
				setCurrentPageBodyAttribute(current);
			}
		);
	}

	/**
	 * @ngdoc method
	 * @name RoutingService#findByUrl
	 *
	 * @description
	 * Recognizes if the url is a in app routing paths.
	 * ToDo: Will work only with paths without optional parameters on it
	 *
	 * @param {string} url Url to search
	 */
	findByUrl(url: string) {
		const _url = url.replace(/\s/gi, '%20');
		const $state = this.$state;

		return $state.get().find((item) => {
			const reg1 = /[^/]+/gi;
			const reg2 = /[^/]+/gi;
			const rUrl = $state.href(item.name || '');

			let result1 = reg1.exec(_url);
			let result2 = reg2.exec(rUrl);
			let isValidItem = true;

			while (result1 && result2) {
				if (result1[0] !== result2[0]) {
					isValidItem = false;
				}
				result1 = reg1.exec(_url);
				result2 = reg2.exec(rUrl);
			}

			if (isValidItem && (result1 || result2)) {
				isValidItem = false;
			}

			return isValidItem;
		}) as NamedStateDeclaration;
	}

	/**
	 * @ngdoc method
	 * @name RoutingService#find
	 *
	 * @description
	 * Gets the Routing Item by specified route path
	 *
	 * @param {string} route Route path
	 */
	find(route?: string) {
		if (!route) {
			return null;
		}

		return this.$state.get().find((item) => item.name === route) as NamedStateDeclaration;
	}

	/**
	 * @ngdoc method
	 * @name RoutingService#getCurrent
	 *
	 * @description
	 * Gets current route
	 */
	getCurrent() {
		return Object.assign({}, (this.$state || {}).current, {
			parameters: this.$state.params,
		});
	}

	/**
	 * @ngdoc method
	 * @name RoutingService#isCurrent
	 *
	 * @description
	 * Tells if a route with specified parameters is a current one
	 *
	 * @param {string} name - name of a route to be checked
	 * @param {object} params - parameters of a route to be checked
	 */
	isCurrent(name: string, params: StateParams) {
		return this.$state.is(name, params || {});
	}

	/**
	 * @ngdoc method
	 * @name RoutingService#go
	 *
	 * @description
	 * Opens specified Routing
	 *
	 * @returns {promise} A promise representing the state of the new transition
	 *
	 * @param {string} route Specifies the Routing Item, either by name or {@link RoutingItem Routing object}
	 * @param {string} queryParams Additional query parameters
	 * @param {object} params Specifies the Routing Item parameters
	 */
	go(route?: string | RoutingItem, params?: StateParams, queryParams?: string) {
		if (!route) {
			throw new Error('Routing service. cannot navigate to an empty object');
		}
		let routeName;

		if (typeof route === 'string') {
			routeName = route;
		} else if (typeof route === 'object') {
			routeName = route.name || '';
		}
		return this.navigate(routeName, params, queryParams);
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.RoutingService#goToDefault
	 *
	 * @description
	 * Tries to navigate to default route
	 *
	 */
	goToDefault(queryParams?: string) {
		const defaultRoute = this.defaultRoute;

		if (defaultRoute && defaultRoute.name) {
			this.go(defaultRoute.name, defaultRoute.parameters || {}, queryParams);
		}

		// this.saveLastVisited();
	}

	getDefaultRouteItem() {
		return this.defaultRoute;
	}

	goToUrl(url = '', inNewWindow = false) {
		const normalizedUrl = this._normalizeUrl(url);
		if (!normalizedUrl) {
			console.error(`url doesn't exist`);
			return false;
		}
		if (inNewWindow) {
			this.$window.open(normalizedUrl);
		} else {
			this.$window.location.href = normalizedUrl;
		}
		return true;
	}

	_normalizeUrl(param: unknown): string {
		if (typeof param !== 'string') {
			return '';
		}
		let url = param;
		if (!url || url.startsWith('http')) {
			return url;
		}

		let normalizedBasePath = this.config.get('basePath');

		if (typeof normalizedBasePath !== 'string' || !normalizedBasePath.length) {
			normalizedBasePath = '/';
		} else {
			normalizedBasePath = normalizedBasePath.toLowerCase();
		}

		if (!normalizedBasePath.startsWith('/')) {
			normalizedBasePath = '/' + normalizedBasePath;
		}

		if (!normalizedBasePath.endsWith('/')) {
			normalizedBasePath = normalizedBasePath + '/';
		}

		if (url.startsWith('/')) {
			url = url.slice(1, url.length);
		}

		if (normalizedBasePath.length > 1 && url.startsWith(normalizedBasePath.slice(1, normalizedBasePath.length))) {
			normalizedBasePath = '/';
		}

		url = `${window.location.origin}${normalizedBasePath}${url}`;
		return url;
	}

	navigate(routeName?: string, routeParams?: RawParams, queryParams?: string) {
		const defer = this.$q.defer();
		const state = this.find(routeName);

		if (state) {
			if (typeof state.templateUrl === 'string' && state.templateUrl.indexOf('http') === 0) {
				this.$window.location.href = state.templateUrl + '?' + queryParams;
				defer.resolve(true);
			} else {
				this.$state
					.go(state.name || '', routeParams, queryParams ? { location: false } : undefined)
					.then(function (res) {
						defer.resolve(res);
					})
					.catch((e) => {
						defer.reject(e);
					});
			}
		} else {
			throw new Error('Routing service. Cannot navigate - route is not found');
		}
		return defer.promise;
	}

	/**
	 * @ngdoc method
	 * @name RoutingService#reload
	 *
	 * @description
	 * Reloads current state
	 *
	 */
	reload() {
		// Using method `$state.reload()` would lead to re-initing whole mxShell which causes diff problems, like duplicating Global Event Subscriptions, etc.
		return this.$state.transitionTo(this.$state.current, this.$state.params, {
			reload: this.$state.current.name, // using state name instead of `true` allows to avoid reIniting whole mxShell
			inherit: false,
		});
	}

	/**
	 * @ngdoc method
	 * @name RoutingService#back
	 *
	 * @description
	 * Opens last history item
	 *
	 */
	async back() {
		const last = this.history.last;

		if (last) {
			return await this.go(last.state.name, last.parameters);
		}
		return;
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.RoutingService#addItems
	 *
	 * @description
	 * Extends routing collection with routes. Only routes with unique {RoutingItem#name name}
	 * will be added.
	 *
	 * @param {RoutingItem[]} items List of RoutingItem
	 */
	addItems(items: RoutingItem[]) {
		const routeConfigItems = [] as NamedStateDeclaration[];

		items.forEach((item) => {
			if (!(item instanceof RoutingItem)) {
				return;
			}

			if (item.name && !Object.prototype.hasOwnProperty.call(this.routeConfigs, item.name)) {
				const state = item.toState();
				routeConfigItems.push(state);
				this.routeConfigs[item.name] = state;
			}
		});
		if (this.stateProvidernitialized) {
			routeConfigItems.forEach((item) => this.$stateProvider.state(item.name || '', item));
		}
	}

	initShellStates(shellState: NamedStateDeclaration) {
		Object.values(this.routeConfigs).forEach((item) => {
			item.parent = shellState.name;
			this.$stateProvider.state(item.name, item);
		});

		this.$stateProvider.state(shellState.name, shellState);

		this.$urlRouterProvider.otherwise(() => {
			const basePath = this.config.get('basePath').toLowerCase();
			const path = window.location.pathname.toLowerCase();

			if (path.lastIndexOf(basePath) === path.length - basePath.length || /view-options=(.+?)(?:&|$)/gi.exec(window.location.search)) {
				this.goToDefault(window.location.search.substring(1));
			} else if (/[^?]*\/app-[^?/]*\/global-search\/[^?/]*/.test(path)) {
				// when switching from "Classic Look" Search Results Page to "New Look"
				this.$timeout((): void => {
					this.goToDefault();
				});
			} else {
				this.go('404');
			}
		});

		this.stateProvidernitialized = true;
	}

	/**
	 * @ngdoc method
	 * @name RoutingService#createRoutingItem
	 *
	 * @description
	 * Create createRoutingItem with options
	 *
	 * @param {object} options - Options for RoutingItem
	 * @param {string} [options.name] - Gets or sets Unique name of the Routing item
	 * @param {string} [options.controller] - Gets or sets controller name for the Routing item. Routing item name + 'Controller' is used by default
	 * @param {string} [options.viewUrl] - Gets or sets Routing item view url
	 * @param {string} [options.template] - Gets or sets Routing item template
	 * @param {string} [options.component] - Gets or sets Component for the Routing item. Uses instead of controller and template
	 * @param {string} [options.path] - Gets or sets path for the Routing item. '/' + routing item name is used by default.
	 * @param {string} [options.title] - Gets or sets title for the Routing item. '/' + routing item name is used by default
	 * @param {boolean} [options.unauthorized] - Possibility to open a route without authorization
	 * @param {Object} [options.resolve] - Gets or sets object with resolve options
	 * @param {Object} [options.params] - Object with default values of parameters in the path
	 * @param {function|string} [options.redirectTo] - either name of state or function with input parameter '$transition$' of ui-router to make optional redirect
	 */
	createRoutingItem(options: RoutingItemOptions) {
		return new RoutingItem(options);
	}

	public getRouteItemUrl(item: BaseRouteType) {
		return this.$state.href(item.name, item.parameters, { absolute: true });
	}
}

export class RoutingServiceProvider {
	static readonly $inject = ['$stateProvider', '$urlRouterProvider'];

	constructor(
		private readonly $stateProvider: StateProvider,
		private readonly $urlRouterProvider: UrlRouterProvider
	) {}

	$get = [
		'$timeout',
		'$q',
		'$window',
		'$state',
		'$urlRouter',
		'mx.shell.Config',
		'mx.shell.EventService',
		'$transitions',
		(
			$timeout: ITimeoutService,
			$q: IQService,
			$window: IWindowService,
			$state: StateService,
			$urlRouter: UrlRouter,
			config: ConfigService,
			eventService: EventService,
			$transitions: TransitionService
		) => new RoutingService($timeout, $q, $window, $state, $urlRouter, config, eventService, $transitions, this.$stateProvider, this.$urlRouterProvider),
	];
}
